/**
 * @fileoverview Servicio utilitario para renderizar color y nombre de equipo en respuestas rápidas.
 * Proporciona funciones memoizadas para obtener el color y nombre del equipo asociado a una respuesta rápida.
 * Cumple con optimización de performance (useMemo) y separación de responsabilidades.
 *
 * @module QuickAnswersServiceMC
 * @category Service - Utility
 * @since 2.0.0
 * @version 3.1.0
 */

/**
 * @typedef {Object} Team
 * @description Estructura de un equipo asociado a una respuesta rápida.
 * @property {string} name - Nombre del equipo.
 * @property {string} color - Color representativo del equipo (hex).
 */

/**
 * @typedef {Object} AnswerWithTeam
 * @description Respuesta rápida con información de equipo.
 * @property {Team} [team] - Equipo asociado (opcional).
 * @property {boolean} [global] - Indica si es global.
 */

import React, { useMemo } from "react";
import { isEmpty } from "lodash";


/**
 * Renderiza el color del equipo asociado a la respuesta rápida.
 *
 * @function renderTeamColor
 * @memberof QuickAnswersServiceMC
 * @param {AnswerWithTeam} answer - Respuesta rápida con información de equipo
 * @returns {JSX.Element} Elemento visual del color del equipo
 *
 * @example
 * <>{renderTeamColor(answer)}</>
 *
 * @since 2.0.0
 * @version 3.1.0 - Documentación JSDoc completa y optimización de performance
 */
export const renderTeamColor = (answer) => {
  const bgColor = () => {
    if (!isEmpty(answer.team?.color)) {
      return answer.team.color;
    } else if (answer.global) {
      return '#7BD6A2';
    } else {
      return '#3CAAE1';
    }
  };

  return (
    <span 
      className="team-color tw-inline-block tw-w-3 tw-h-3 tw-rounded-full" 
      style={{ backgroundColor: bgColor() }}
      role="presentation"
      aria-label="Team color indicator"
    />
  );
};


/**
 * Renderiza el nombre del equipo asociado a la respuesta rápida.
 *
 * @function renderTeamName
 * @memberof QuickAnswersServiceMC
 * @param {AnswerWithTeam} answer - Respuesta rápida con información de equipo
 * @param {function} t - Función de traducción i18n
 * @returns {string} Nombre del equipo traducido o etiqueta global/personal
 *
 * @example
 * <>{renderTeamName(answer, t)}</>
 *
 * @since 2.0.0
 * @version 3.1.0 - Documentación JSDoc completa y optimización de performance
 */
export const renderTeamName = (answer, t) => {
  if (!isEmpty(answer.team?.name)) return answer.team?.name;
  if (answer.global) return t('all');
  return t('personal');
};